import type { CSSProperties } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * One rolling digit: a column of 0–9 behind a one-figure window.
 *
 * The column is exactly ten cells tall, so `-digit * 10%` lands the wanted numeral dead centre, and the
 * transition rolls through everything in between — 2 to 7 passes 3, 4, 5 and 6, which is what reads as
 * an odometer rather than as a swap. Every cell MUST be the same height or that arithmetic stops landing
 * on a digit, which is why the height lives in `cell` and is shared by all ten.
 *
 * ALL CSS: the roll is a transition on the `translate` property and nothing here re-renders to animate.
 * The easing does NOT overshoot — the digit decelerates onto its slot and stops there. It is an ease-OUT
 * rather than the ease-in-out a slide zoom uses: a digit leaves at speed because it is travelling, in a
 * straight line, a distance the reader is not meant to follow. `motion-reduce` takes the roll away for a visitor who asked
 * for less motion — the digit still changes, it just arrives, and that is one media query rather than a
 * hook feeding a prop down.
 *
 * Shared by the Solutions donation counter and the About timeline's year figure, which want the same
 * roll at wildly different sizes — hence `cell` for the type and `className` for the window, rather than
 * anything baked in here.
 */
export function DigitRoll(props: { digit: number; cell: string; className?: string }) {
	return (
		<span className={cn('relative inline-block overflow-hidden', props.className)}>
			{/* An invisible copy of THIS digit, in normal flow, there only to size the window: the column is
			    absolute and so cannot size its own parent.

			    It is the current digit rather than a fixed `0` so the window always takes the width of the
			    glyph actually being shown. On the brand face that now costs nothing — Rethink Sans's digits
			    are a uniform 600 units, so every slot comes out the same width anyway — but it still earns
			    its place during `font-display: swap`, when the text is painted in whatever generic sans the
			    system supplies and those digits are NOT tabular. Sizing to a fixed `0` there would cut every
			    slot to one digit's width and space `2014` out into `2 0 1 4` until the real face arrived. */}
			<span aria-hidden className={cn('invisible', props.cell)}>
				{props.digit}
			</span>
			{/* CENTRED on the window rather than filling it, which is what lets the window be narrower than
			    the widest cell: a `1` shows through its own narrow slot, and the digits passing during a roll
			    are clipped at their side bearings — trimming air, never ink. Both halves of the offset ride
			    the one `translate` property, so the horizontal centring cannot be lost when the roll moves;
			    a `-translate-x-1/2` class would be writing the same property from the other side. */}
			<span
				aria-hidden
				style={{ translate: `-50% ${-props.digit * 10}%` } as CSSProperties}
				className="absolute top-0 left-1/2 flex flex-col items-center transition-[translate] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
			>
				{DIGITS.map((d) => (
					<span key={d} className={cn('whitespace-nowrap', props.cell)}>
						{d}
					</span>
				))}
			</span>
		</span>
	);
}
